import { DeviceTypeEnum } from "src/app/device/shared/enums/device-type.enum";
import { LedModeType } from "src/app/device/shared/enums/led-mode-type";
import { WarnLevelEnum } from "src/app/device/shared/enums/warn-level.enum";
import { BarrierPositionEnum } from "src/app/device/shared/enums/barrier-position.enum";
import { RadarType } from "src/app/device/shared/interfaces/radar-type.interface";
import { WeatherDataItem } from "src/app/device/shared/interfaces/weatherDataItem";
import { LatestVoltage } from "../../../shared/interfaces/latest-voltage.interface";
import { DataEndChannelTypeEnum } from "../../../shared/enums/data-end-channel-type.enum";
import { EngineTriggerTypeEnum } from "../../../shared/enums/engine-trigger-type.enum";
import { EngineTrigger } from "../../../shared/interfaces/engine-trigger.interface";
import { SchemaIndicatorEnum } from "../enums/schema-indicator.enum";

/**
 * Vom Backend vorbereitete Schema-Struktur pro Station
 * (AVS_Web-API: Models/Schema/SchemaRouteStationInfo.cs, RPA-1479).
 */
export interface SchemaRouteStationInfo {
    id: string,
    samId: number,
    name: string,
    x: number,
    y: number,
    z: number,
    warnLevel: WarnLevelEnum,
    /** EVA-Verbindungsstatus (TLSoIP-Verbindung zum EVA_Server) */
    online: boolean,
    /** Geräteart — Render-Weiche der Kachel */
    deviceType: DeviceTypeEnum,
    /** Zeitpunkt der letzten Daten der Station */
    lastData?: string | null,
    gps: SchemaGpsInfo,
    solar?: SchemaSolarInfo | null,
    sim?: SchemaSimInfo | null,
    digitalIOs: SchemaDioInfo[],
    displays?: SchemaDisplayInfo[] | null,
    lamp: SchemaLampInfo,
    radars: SchemaRadarInfo[],
    fuelcell: SchemaFuelcellInfo,
    cameras: SchemaCamInfo[],
    barrier: SchemaBarrierInfo,
    weather: SchemaWeatherInfo
}

/** Kanal-Kontext einer Schema-Komponente (DE, Fg, Kanalfehler-Status, Geräteinformationen). */
export interface SchemaComponentInfo {
    channel: SchemaDataEndChannelInfo,
    status?: SchemaStatusLevel | null,
    /** Geräteinformationen des angeschlossenen Geräts (API: TlsDevice), sofern bekannt */
    device?: SchemaDeviceInfo | null
}

/** Entspricht TlsDevice der API (Seriennummer, Firmware-/Softwareversion, Gerätename). */
export interface SchemaDeviceInfo {
    serialNumber?: string | null,
    version?: string | null,
    deviceName?: string | null
}

export interface SchemaDataEndChannelInfo {
    de: number,
    localbusAddress: number,
    fg: number,
    deType: DataEndChannelTypeEnum,
    isInChannelError?: boolean | null
}

/** Entspricht TlsResponse.StatusLevel der API. */
export enum SchemaStatusLevel {
    Ok = 0,
    Warning = 1,
    Error = 2
}

export interface SchemaGpsInfo {
    data?: { latitude: number, longitude: number, dataDateTime: string } | null,
    cInfo: SchemaComponentInfo
}

export interface SolarInfo {
    batteryVoltage?: number | null,
    batteryCurrent?: number | null,
    loadCurrent?: number | null,
    markedToBeOutdated?: boolean | null,
    /** Letzter bekannter Spannungswert (auch wenn die Station offline ist) */
    latestVoltage?: LatestVoltage | null,
    dataDateTime: string
}

export interface SchemaSolarInfo {
    data?: SolarInfo | null,
    cInfos: SchemaComponentInfo[]
}

export interface SchemaSimInfo {
    data?: { iccId?: string | null, signal?: number | null, provider: string, connType?: string | null, dataDateTime: string } | null,
    cInfo: SchemaComponentInfo
}

export interface DiResultInfo {
    trigger: EngineTriggerTypeEnum,
    active: boolean,
    userId: number,
    dataDateTime: string
}

export interface DigitalIoInfo {
    activeStatus?: boolean | null,
    dataDateTime: string,
    diResult?: DiResultInfo | null
}

export interface SchemaDioInfo {
    data?: DigitalIoInfo | null,
    cInfo: SchemaComponentInfo
}

export interface DisplayBaseInfo {
    indicator: SchemaIndicatorEnum,
    code: number,
    dataDateTime: string,
    engineResult?: EngineTrigger | null
}

export interface LedBrightnessInfo {
    maxBrightness?: number | null,
    brightness: number,
    mode: LedModeType,
    spontanBrightnessMsgs: boolean,
    dataDateTime: string
}

export interface DisplayInfo {
    ledBrightness?: LedBrightnessInfo | null,
    display?: DisplayBaseInfo | null,
    imagePaths: string[],
    uploadOngoing: boolean,
    codeError: boolean,
    isAnimated: boolean,
    dataDateTime: string
}

export interface SchemaDisplayInfo {
    data?: DisplayInfo | null,
    cInfo: SchemaComponentInfo
}

export interface SchemaLampInfo {
    data?: DisplayBaseInfo | null,
    cInfo: SchemaComponentInfo
}

export interface MqResultInfo {
    trigger: EngineTriggerTypeEnum,
    userId: number,
    trafficData: string,
    z: number,
    vs: number,
    line: number,
    dataDateTime: string
}

export interface RadarInfo {
    allTrafficVolume: number,
    truckTrafficVolume: number,
    carAverageSpeed: number,
    truckAverageSpeed: number,
    allSmoothedAverageSpeed: number,
    allStdDeviation: number,
    utilization: number,
    meanNetTimeGap: number,
    dataDateTime: string,
    radarType?: RadarType | null,
    mqResult?: MqResultInfo | null
}

export interface SchemaRadarInfo {
    data?: RadarInfo | null,
    cInfo: SchemaComponentInfo
}

export interface FuelcellInfo {
    batteryLoad: number,
    batteryVoltage: number,
    generatorPower: number,
    generatorVoltage: number,
    generatorCurrent: number,
    externalFuelSensorAlarm: boolean,
    fuelLevel: number[],
    nbActiveCart: number,
    dataDateTime: string
}

export interface SchemaFuelcellInfo {
    data?: FuelcellInfo | null,
    /** Setting of the fuel cell channel: the message of the external fuel sensor is not shown in the schema */
    ignoreExternalFuelSensor: boolean,
    cInfo: SchemaComponentInfo
}

export interface SchemaCamInfo {
    camType?: DataEndChannelTypeEnum | null,
    cInfo: SchemaComponentInfo
}

export interface BarrierInfo {
    dataDateTime: string,
    warmUp?: boolean | null,
    errorCode?: boolean | null,
    obstacleDetected?: boolean | null,
    localControlEnabled?: boolean | null,
    securityLockEnabled?: boolean | null,
    /** BarrierDeviceType der API: 0=None, 1=LaneSwitch, 2=Bar, 3=Swing */
    barrierDeviceType: number,
    positioningStatus?: BarrierPositionEnum | null
}

export interface SchemaBarrierInfo {
    data?: BarrierInfo | null,
    cInfo: SchemaComponentInfo,
    /** Letzte Engine-Schaltung der Schranke (auch ohne data vorhanden) */
    engineResult?: EngineTrigger | null
}

export interface SchemaWeatherInfo {
    data?: (WeatherDataItem & { dataDateTime: string }) | null,
    cInfo: SchemaComponentInfo
}
